<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { twMerge } from 'tailwind-merge';
import { computed } from 'vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import TextHeadline from '../atoms/app/text/TextHeadline.vue';

const props = withDefaults(defineProps<{
    initialFocus?: HTMLElement | null;
    panelClasses?: string;
    containsIframe?: boolean;
    preventDismiss?: boolean;
    showTitle?: boolean;
    }>(), {
    panelClasses: '',
    containsIframe: false,
    preventDismiss: false,
    showTitle: true,
});

const open = defineModel<boolean>('open');
const emit = defineEmits(['after-leave']);

function afterLeave() {
    open.value = false;
    emit('after-leave');
}

const baseClasses = 'max-w-[550px] p-3xl';

// Use twMerge to combine base and conditional classes.
// Classes are overridden by those listed later.
// Add new conditionals as needed, ensuring specific styles are last.
const combinedClasses = computed(() => twMerge(
    baseClasses,
    props.containsIframe && 'h-full overflow-y-hidden max-w-[880px] pt-l px-0 pb-0',
    props.panelClasses,
));

function handleClose() {
    if (!props.preventDismiss) {
        open.value = false;
    }
}
</script>

<template>
    <TransitionRoot
        :show="open"
        as="template"
        @after-leave="afterLeave"
    >
        <Dialog
            :initial-focus
            @close="handleClose"
        >
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-gray-900-40 z-[700]"
                    aria-hidden="true"
                />
            </TransitionChild>
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0 -translate-y-2xl"
                enter-to="opacity-100 scale-100 translate-y-0"
                leave="duration-200 ease-in"
                leave-from="opacity-100 translate-y-0"
                leave-to="opacity-0 -translate-y-4xl"
            >
                <div class="fixed inset-0 flex w-screen items-center justify-center p-l z-[800] max-h-full">
                    <DialogPanel
                        class="relative w-full rounded-md
                               bg-white inset-0 z-[800] max-h-full overflow-y-auto"
                        :class="combinedClasses"
                        data-qa="cv extraction modal visible"
                    >
                        <button
                            v-if="!preventDismiss"
                            class="leading-[0] absolute top-l right-l w-l h-l text-gray-600 cursor-pointer z-10"
                            data-qa="close button"
                            type="button"
                            aria-label="Dialog schließen"
                            @click="handleClose"
                        >
                            <AppIcon name="cross"/>
                        </button>
                        <DialogTitle
                            v-if="showTitle"
                        >
                            <TextHeadline
                                class="text-center mb-l"
                                :class="containsIframe && 'mb-xl'"
                                variant="md-700"
                            >
                                <slot name="headline"/>
                            </TextHeadline>
                        </DialogTitle>
                        <p
                            class="text-center"
                            :class="containsIframe && 'h-full'"
                        >
                            <slot/>
                        </p>
                        <div
                            v-if="$hasSlotContent($slots.actions)"
                            class="mt-l text-center"
                        >
                            <slot
                                name="actions"
                            />
                        </div>
                    </DialogPanel>
                </div>
            </TransitionChild>
        </Dialog>
    </TransitionRoot>
</template>

<style lang="scss" scoped>
.bg-gray-900-40 {
    background-color: rgba(23, 25, 28, 0.4);
}
</style>
